import { useCallback, useEffect, useRef, useState } from 'react';
import { EmailPreviewIframeRef } from './EmailPreviewIframe';
import { InvitationEmailEditHintPosition } from './generateInvitationEmailTypes';
import { computeEditHintPosition } from './generateInvitationEmailUtils';

/**
 * Click-to-edit "speech bubble" hint for an editable region inside
 * EmailPreviewIframe — shared between useGenerateInvitationEmail and
 * useStatementEmailCompose (both drive the same iframe rich-text preview
 * infra). Shows a one-time hint pointing at the editable content, either
 * immediately on hover-position or after a short delay if the admin hasn't
 * interacted yet, and stays hidden for the rest of the session once they
 * have (handleEnterEditMode / handleHideSpeechBubble).
 */
export function useEditHintSpeechBubble() {
  const emailPreviewRef = useRef<EmailPreviewIframeRef>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [hasClickedEditable, setHasClickedEditable] = useState(false);
  const [elementPosition, setElementPosition] = useState<InvitationEmailEditHintPosition | null>(
    null,
  );

  const handleEnterEditMode = useCallback(() => {
    setHasClickedEditable((prev) => (prev ? prev : true));
    setShowSpeechBubble(false);
  }, []);

  const handleElementPosition = useCallback(
    (position: { top: number; left: number }) => {
      const containerEl = iframeContainerRef.current;
      if (!containerEl) {
        return;
      }
      const normalizedPosition = computeEditHintPosition(containerEl, position);
      if (normalizedPosition) {
        setElementPosition(normalizedPosition);
        if (!hasClickedEditable) {
          setShowSpeechBubble(true);
        }
      } else {
        setElementPosition(null);
        setShowSpeechBubble(false);
      }
    },
    [hasClickedEditable],
  );

  const handleHideSpeechBubble = useCallback(() => {
    if (!hasClickedEditable) {
      setHasClickedEditable(true);
      setShowSpeechBubble(false);
    }
  }, [hasClickedEditable]);

  useEffect(() => {
    if (elementPosition && !hasClickedEditable) {
      const timer = setTimeout(() => setShowSpeechBubble(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [elementPosition, hasClickedEditable]);

  const resetEditHintState = useCallback(() => {
    setShowSpeechBubble(false);
    setElementPosition(null);
    setHasClickedEditable(false);
  }, []);

  return {
    emailPreviewRef,
    iframeContainerRef,
    showSpeechBubble,
    elementPosition,
    handleEnterEditMode,
    handleElementPosition,
    handleHideSpeechBubble,
    resetEditHintState,
  };
}
